// tempexp_18907-full-file
import { isSameDay } from 'date-fns';
import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IS_AUTO_TEST_HIDE } from 'reactApp/appHelpers/configHelpers';
import { isSaveDiscountInOutlowEnabled } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { getBillingDataRequestIfNotLoaded } from 'reactApp/modules/billingData/billingData.module';
import { BillingDataSelectors, getBillingDataLoadingState } from 'reactApp/modules/billingData/billingData.selectors';
import { BillingDataSubscriptionInfoStatus } from 'reactApp/modules/billingData/billingData.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { getCurrentStorage } from 'reactApp/modules/router/router.selectors';
import { getStorage } from 'reactApp/modules/storage/storage.helpers';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import type { RootState } from 'reactApp/store';
import {
    type DiscountInOutflowLSData,
    clearDiscountInOtflow,
    DISCOUNT_IN_OUTFLOW,
    DISCOUNT_IN_OUTFLOW_TIMER,
    discountInOutflowLogger,
} from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { getTimeToDate } from 'reactApp/utils/timeHelpers';

import { DiscountTimer } from './DiscountTimer';

export interface DiscountInOutflowSaveData {
    show: boolean;
    subId: number;
}

const useDiscountSaveData = () => {
    const isCorpUser = useSelector(UserSelectors.isCorpUser);
    const isBizUser = useSelector(UserSelectors.isBizUser);
    const isSocialUser = useSelector(UserSelectors.isSocialUser);
    const discountFlagValue = storeHelper.getValue(DISCOUNT_IN_OUTFLOW);
    const subId = discountFlagValue?.subId;
    const expirationDate = new Date(discountFlagValue?.expirationDate);
    const isEnded = getTimeToDate(expirationDate) < 0;
    const dispatch = useDispatch();

    const hideConditionPart =
        !isSaveDiscountInOutlowEnabled || isCorpUser || isBizUser || isSocialUser || IS_AUTO_TEST_HIDE || !discountFlagValue || isEnded;

    useEffect(() => {
        // если не выполнились предыдущие условия, то наличие активной
        // подписки уже неважно и можно не делать лишние запросы
        if (!hideConditionPart) {
            discountInOutflowLogger.info('load billing data (memo)');

            dispatch(getBillingDataRequestIfNotLoaded());
        }
    }, []);

    const { isLoaded: isBillingLoaded } = useSelector(getBillingDataLoadingState);
    const subscription = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsById(state, subId));
    const isAlreadyActive = subscription?.status === BillingDataSubscriptionInfoStatus.ACTIVE;

    if (isEnded || (isBillingLoaded && isAlreadyActive)) {
        clearDiscountInOtflow();
    }

    const isLoaded = hideConditionPart || isBillingLoaded;
    const hideCondition = hideConditionPart || !isLoaded || !subscription || isAlreadyActive;

    return {
        isLoaded,
        show: !hideCondition,
    };
};

export const useDiscountTimer = (setVisible: (visibility: boolean) => void) => {
    const storage = useSelector(getCurrentStorage);
    const { isHome } = getStorage(storage);

    const discountInOutflowData: DiscountInOutflowLSData = useMemo(() => {
        return storeHelper.getValue(DISCOUNT_IN_OUTFLOW) || {};
    }, []);

    const expirationDate = new Date(discountInOutflowData.expirationDate);
    const subId = discountInOutflowData.subId;

    const { show: discountSaveCondition, isLoaded } = useDiscountSaveData();
    const subscription = useSelector((state: RootState) => BillingDataSelectors.getSubscriptionsById(state, subId));

    const timerData = storeHelper.getValue(DISCOUNT_IN_OUTFLOW_TIMER);
    const isSameDayClosed = !!timerData && isSameDay(new Date(timerData?.closed), new Date());

    if (!discountSaveCondition || !isHome || isSameDayClosed) {
        discountInOutflowLogger.error('timer inner cond fail', { isLoaded, isSameDayClosed, discountSaveCondition });
        return { isLoaded, element: null };
    }
    discountInOutflowLogger.ok('timer inner cond success');

    const timer = <DiscountTimer expirationDate={expirationDate} setVisible={setVisible} subscription={subscription!} />;

    return { isLoaded, element: timer };
};
